import React from "react";
import { Helmet } from "react-helmet-async";
import { type Lang, langFromPath, manifest } from "@/i18n/lang";
import { alternateEntries } from "@/i18n/route-policy.js";
import businessFacts from "@/data/businessFacts.json";
import { openingHoursSpecification } from "@/lib/businessHours";
import { publishedSchema } from "@/lib/schemaPolicy.js";
import { businessSeoTitle } from "@/lib/seoTitle.js";
import { INTERNATIONAL_COPY } from "@/data/internationalConversion";
import { OFFICIAL_EMAIL, TECHNICIAN_NAME_EN, TECHNICIAN_NAME_HE, SERVICE_LANGUAGES } from "@/data/officialIdentity.js";

export const BASE_URL = manifest.origin;

interface BreadcrumbItem {
  name: string;
  url: string;
}

type OgType = "website" | "article" | "profile" | "product";

interface SEOHeadProps {
  title?: string;
  description?: string;
  canonical?: string;
  noindex?: boolean;
  breadcrumbs?: BreadcrumbItem[];
  schemaData?: unknown[];
  geoPlacename?: string;
  ogImage?: string;
  ogType?: OgType;
  /** Hebrew page that has an English mirror at the same path under /en. */
  hasEnglish?: boolean;
  availableLangs?: Lang[];
  alternatePaths?: Partial<Record<Lang, string>>;
  /** Disable the Hebrew sitewide Person/Service schemas when a localized page supplies its own schema. */
  includeGlobalSchemas?: boolean;
}

/** Deduplicate JSON-LD objects. Prefers @id+@type as the dedupe key when both exist,
 *  falls back to a stable JSON stringification. Never throws on circular refs. */
function dedupeSchemas(schemas: unknown[]): unknown[] {
  const seen = new Set<string>();
  const out: unknown[] = [];
  for (const s of schemas) {
    if (!s || typeof s !== "object") continue;
    const obj = s as Record<string, unknown>;
    let key: string;
    if (obj["@id"]) {
      key = `id:${String(obj["@id"])}|t:${String(obj["@type"] ?? "")}`;
    } else {
      try {
        key = JSON.stringify(obj);
      } catch {
        key = `${String(obj["@type"] ?? "")}|${Math.random()}`;
      }
    }
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(obj);
  }
  return out;
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  canonical,
  noindex = false,
  breadcrumbs,
  schemaData = [],
  geoPlacename = "ירושלים ומבשרת ציון",
  ogImage,
  ogType = "website",
  hasEnglish = false,
  availableLangs,
  alternatePaths,
  includeGlobalSchemas = langFromPath(typeof window !== "undefined" ? window.location.pathname : "/") === "he",
}) => {
  const currentPath = typeof window !== "undefined" ? window.location.pathname : "/";
  const seoCopy = INTERNATIONAL_COPY[langFromPath(currentPath)];
  title = businessSeoTitle(title?.trim() || seoCopy.title);
  description = description?.trim() || seoCopy.description;

  /** Normalize to an absolute, query-free URL with no trailing slash (except the root). */
  const buildCanonical = (value: string): string => {
    const absolute = value.startsWith("http")
      ? `${BASE_URL}${new URL(value).pathname}`
      : `${BASE_URL}${value.startsWith("/") ? "" : "/"}${value}`;
    const withoutQuery = absolute.split("?")[0].split("#")[0];
    const trimmed = withoutQuery.replace(/\/+$/, "");
    return trimmed === BASE_URL || trimmed === "" ? BASE_URL : trimmed;
  };

  const fullCanonicalUrl = buildCanonical(canonical || currentPath);


  // 1. BreadcrumbList — only when caller provides items
  const breadcrumbSchema = breadcrumbs && breadcrumbs.length > 0
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: item.name,
          item: item.url.startsWith("http")
            ? item.url
            : `${BASE_URL}${item.url.startsWith("/") ? "" : "/"}${item.url}`,
        })),
      }
    : null;

  // 2. Person (E-E-A-T) — real profile, real social links
  const EXPERT_PERSON_SCHEMA = {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": `${BASE_URL}/#person`,
    name: TECHNICIAN_NAME_HE,
    alternateName: TECHNICIAN_NAME_EN,
    jobTitle: "טכנאי סלולר מוסמך דרג א׳–ד׳",
    alumniOf: { "@type": "EducationalOrganization", name: businessFacts.certification.school },
    url: BASE_URL,
    image: `${BASE_URL}/ofir-profile.jpg`,
    sameAs: [
      "https://www.facebook.com/share/1BzGZFhyBL/?mibextid=wwXIfr",
      "https://www.instagram.com/ofir_hershco?igsh=cTh6aGc0NTh4NG1n&utm_source=qr",
      "https://www.tiktok.com/@ofi_x_mobile?_r=1&_t=ZS-98uBP0b0xCm",
    ],
    knowsAbout: [
      "iPhone Repair",
      "Samsung Galaxy Repair",
      "Mobile Electronics",
      "Smartphone Hardware",
      "Jerusalem Local Services",
    ],
  };

  // 3. Service Area Business — Service + LocalBusiness provider.
  //    NO price/priceRange/aggregateRating (no real source) per project policy.
  const SAB_SERVICE_SCHEMA = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${BASE_URL}/#service`,
    serviceType: "Mobile Phone Repair",
    name: "אופיקס O-FIX - תיקון סלולר עד הבית",
    description:
      "שירות תיקוני סלולר מקצועי המגיע עד לבית או למשרד הלקוח בירושלים והסביבה.",
    provider: {
      "@type": "LocalBusiness",
      "@id": `${BASE_URL}/#business`,
      name: "אופיקס O-FIX",
      alternateName: ["אופיקס", "O-FIX", "Ofix", "או פיקס", "אופיקס תיקון סלולר"],
      image: `${BASE_URL}/logo.png`,
      logo: `${BASE_URL}/logo.png`,
      telephone: "+972585449992",
      email: OFFICIAL_EMAIL,
      founder: { "@type": "Person", name: TECHNICIAN_NAME_EN, alternateName: TECHNICIAN_NAME_HE },
      contactPoint: { "@type": "ContactPoint", contactType: "customer service", telephone: "+972585449992", email: OFFICIAL_EMAIL, availableLanguage: SERVICE_LANGUAGES },
      url: BASE_URL,
      areaServed: "ירושלים והסביבה",
      openingHoursSpecification,


      sameAs: [
        "https://www.facebook.com/share/1BzGZFhyBL/?mibextid=wwXIfr",
        "https://www.instagram.com/ofir_hershco?igsh=cTh6aGc0NTh4NG1n&utm_source=qr",
        "https://www.tiktok.com/@ofi_x_mobile?_r=1&_t=ZS-98uBP0b0xCm",
      ],
      geo: {
        "@type": "GeoCoordinates",
        latitude: 31.7683,
        longitude: 35.2137,
      },
      address: {
        "@type": "PostalAddress",
        addressLocality: "ירושלים",
        addressRegion: "ירושלים",
        addressCountry: "IL",
      },
    },
    provider_mentions: undefined, // dropped — kept comment to signal we removed wikipedia hack
    areaServed: businessFacts.activeAreas.map(area => area.name),
  };
  // strip the placeholder key so it doesn't render
  delete (SAB_SERVICE_SCHEMA as Record<string, unknown>).provider_mentions;

  const allSchemas = dedupeSchemas([
    ...(includeGlobalSchemas ? [EXPERT_PERSON_SCHEMA, SAB_SERVICE_SCHEMA] : []),
    ...(breadcrumbSchema ? [breadcrumbSchema] : []),
    ...schemaData.filter(Boolean).map(publishedSchema),
  ]);

  // Language + hreflang. The English mirror lives under /en; a page declares it has
  // a mirror via `hasEnglish` (Hebrew side) or by being an /en route itself.
  const canonicalPath = fullCanonicalUrl.replace(BASE_URL, "") || "/";
  const pageLang = langFromPath(canonicalPath);
  const alternates = noindex ? [] : alternateEntries(canonicalPath, manifest);

  return (
    <Helmet prioritizeSeoTags>
      <html lang={pageLang === "he" ? "he" : pageLang} dir={pageLang === "he" ? "rtl" : "ltr"} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={noindex ? "noindex, follow" : "index, follow"} />
      <link rel="canonical" href={fullCanonicalUrl} />
      {alternates.map(([lang, href]) => <link key={lang} rel="alternate" hrefLang={lang} href={href} />)}


      {/* Service Area Meta Tags */}
      <meta name="business:type" content="Service Area Business" />
      <meta name="business:service_area" content={geoPlacename} />
      <meta name="geo.region" content="IL-JM" />
      <meta name="geo.placename" content={geoPlacename} />
      <meta name="geo.position" content="31.7683;35.2137" />
      <meta name="ICBM" content="31.7683, 35.2137" />

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={fullCanonicalUrl} />
      {ogImage && <meta property="og:image" content={ogImage} />}
      <meta property="og:type" content={ogType} />
      <meta property="og:locale" content={`${pageLang}_IL`} />
      <meta property="og:site_name" content={INTERNATIONAL_COPY[pageLang].siteName} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}

      {/* AI / LLM Discovery */}
      <link rel="alternate" type="text/plain" title="LLM Summary" href="/llms.txt" />
      <link rel="alternate" type="text/plain" title="Full Knowledge Base" href="/llms-full.txt" />

      {allSchemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEOHead;
